// ============================================================
// PIE — Goals genéricos (lo que el tenant desea optimizar)
// ============================================================
// Cada goal modela PERCEPCIÓN HUMANA (P4), no solo matemática.
// Pure functions 0..1 (mayor = mejor). El tenant solo pondera.
// ============================================================
import type { Goal, PresentationCandidate, SubjectFacts, TenantPieConfig } from '../types';

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

/** Distancia relativa entre dos ratios (0 = iguales). */
function ratioDistance(a: number, b: number): number {
  if (!a || !b) return 0;
  return Math.abs(a - b) / Math.max(a, b);
}

/** Resuelve el ratio numérico de un candidato ('real' → facts.realAspect). */
function candidateRatio(c: PresentationCandidate, facts: SubjectFacts): number | undefined {
  if (c.frame.ratio === 'real') return facts.realAspect;
  const [w, h] = c.frame.ratio.split('/').map(Number);
  if (!w || !h) return undefined;
  return w / h;
}

// ── HARMONY: armonía visual percibida (P4) ──
// Fondo coherente con el sujeto, encuadre limpio, sin colisiones.
// Usa bgUniformity (fondo uniforme = expandible sin fricción visual)
// y dominantColors (coherencia cromática entre frame y foto).
export const harmonyGoal: Goal = {
  id: 'harmony',
  label: 'armonía visual percibida',
  score(c, facts) {
    let s = 0.7; // base: composición limpia por defecto
    if (facts.bgUniformity !== undefined) {
      // Fondo uniforme → backdrop coherente suma armonía
      s += facts.bgUniformity * 0.2;
    }
    if (c.environment.backdrop === 'none') {
      // Sin backdrop: la foto manda — armonía = fondo natural
      s += 0.05;
    } else if (c.environment.backdrop === 'gradient') {
      // Gradiente del color dominante = armonía alta
      s += 0.1;
    } else if (c.environment.backdrop === 'expand') {
      // Expansión bien hecha (con subjectBox) = máxima armonía
      s += facts.subjectBox ? 0.15 : -0.1;
    } else {
      s -= 0.05; // blur sin necesidad = fricción
    }
    return clamp01(s);
  },
};

// ── SUBJECT: protagonismo del sujeto ──
// El producto domina el frame sin perderse. Usa subjectCoverage
// (visión) o realAspect como proxy (frame ≈ foto → sujeto grande).
export const subjectGoal: Goal = {
  id: 'subject',
  label: 'protagonismo del sujeto',
  score(c, facts) {
    if (facts.subjectCoverage !== undefined) {
      return clamp01(facts.subjectCoverage);
    }
    const cr = candidateRatio(c, facts);
    const rr = facts.realAspect;
    if (cr && rr) {
      // frame ≈ aspect real → el sujeto llena el frame (sin letterbox)
      return clamp01(1 - ratioDistance(cr, rr) * 1.5);
    }
    return 0.6;
  },
};

// ── CONSISTENCY: consistencia del catálogo ──
// Desviación del canon del tenant (allowedRatios / params.canonRatios).
export const consistencyGoal: Goal = {
  id: 'consistency',
  label: 'consistencia del catálogo',
  score(c, facts, config) {
    const canon = (config.constraints?.allowedRatios as string[]) ??
      ((config.goals.find(g => g.id === 'consistency')?.params as Record<string, unknown> | undefined)
        ?.canonRatios as string[] | undefined) ??
      ['3/4'];
    const cr = candidateRatio(c, facts);
    if (!cr) return 0.5;
    let best = 1;
    for (const r of canon) {
      const [w, h] = r.split('/').map(Number);
      if (w && h) best = Math.min(best, ratioDistance(cr, w / h));
    }
    return clamp01(1 - best * 2);
  },
};

// ── BREATHING: respiración visual (P4 — NO es desperdicio) ──
// Aire intencional alrededor del sujeto en rango [breathingMin, breathingMax].
export const breathingGoal: Goal = {
  id: 'breathing',
  label: 'respiración visual',
  score(c) {
    const p = c.environment.padding ?? 0;
    const min = 0.03; // 3% por defecto
    const max = 0.12;
    if (p === 0) return 0.3; // sin aire = tenso
    if (p >= min && p <= max) return 1; // respiración ideal
    if (p < min) return 0.5 + p / min * 0.5; // algo de aire, mejor que nada
    return clamp01(1 - (p - max) / max); // demasiado aire = sujeto perdido
  },
};

// ── SPACE: aprovechamiento del espacio ──
// Minimiza vacío que DEGRADA (letterbox sucio). La respiración
// intencional NO se penaliza (P4): solo el desperdicio que degrada.
export const spaceGoal: Goal = {
  id: 'space',
  label: 'aprovechamiento del espacio',
  score(c, facts) {
    const cr = candidateRatio(c, facts);
    const rr = facts.realAspect;
    if (!cr || !rr) return 0.5;
    // Desperdicio = área del frame no ocupada por la foto (contenida)
    let wasted: number;
    if (rr < cr) wasted = 1 - rr / cr;        // bandas laterales
    else wasted = 1 - cr / rr;                // bandas superior/inferior
    // La respiración intencional (padding) no es desperdicio
    const breathingCredit = Math.min((c.environment.padding ?? 0) * 2, 0.2);
    wasted = Math.max(0, wasted - breathingCredit);
    return clamp01(1 - wasted * 1.5);
  },
};

// ── LEGIBILITY: legibilidad de la info asociada ──
// infoTreatment adecuado al infoWeight (hereda la lógica F3 del VCE).
export const legibilityGoal: Goal = {
  id: 'legibility',
  label: 'legibilidad de la información',
  score(c, facts) {
    if (facts.infoWeight === 'heavy') {
      return c.infoTreatment === 'clamped' ? 1 : 0.4;
    }
    if (facts.infoWeight === 'normal') {
      return c.infoTreatment === 'full' || c.infoTreatment === 'compact' ? 1 : 0.6;
    }
    return c.infoTreatment === 'compact' ? 1 : 0.7;
  },
};

export const GOAL_IMPLEMENTATIONS: Record<string, Goal> = {
  harmony: harmonyGoal,
  subject: subjectGoal,
  consistency: consistencyGoal,
  breathing: breathingGoal,
  space: spaceGoal,
  legibility: legibilityGoal,
};
